<template>
  <Dialog
    v-model:visible="searchStore.isSearchDialogVisible"
    @update:visible="searchStore.isSearchDialogVisible = $event"
    modal
    :closable="true"
    :closeOnEscape="true"
    :dismissableMask="true"
    class="max-w-screen-md w-full"
    header="Search Tax Transactions"
    data-test-id="tax-transaction-search-dialog"
  >
    <TaxTransactionSearchFormBase
      ref="formRef"
      :show-merchant-select="true"
      @search="handleSearch"
    >
      <template #actions="{ canSearch, submitSearch, submitButtonLabel }">
        <div class="flex justify-end gap-2">
          <Button
            variant="outlined"
            @click="closeDialog"
            label="Cancel"
            data-test-id="cancel-search-btn"
          />
          <Button
            @click="submitSearch"
            :disabled="!canSearch"
            :label="submitButtonLabel"
            icon="pi pi-search"
            data-test-id="submit-search-btn"
          />
        </div>
      </template>
    </TaxTransactionSearchFormBase>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

import { useSearchStore } from '@/stores/searchStore'
import { FILTER_KEYS } from '@/types/taxTransaction'
import TaxTransactionSearchFormBase from './TaxTransactionSearchFormBase.vue'

const router = useRouter()
const searchStore = useSearchStore()

const formRef = ref<InstanceType<typeof TaxTransactionSearchFormBase> | null>(null)
const searchPerformed = ref(false)

const closeDialog = () => {
  searchStore.clearSelectedMerchant()
  searchStore.closeSearchDialog()
}

const handleSearch = (queryParams: Record<string, string>) => {
  const merchantId = queryParams.merchantId

  if (!merchantId) {
    return
  }

  searchPerformed.value = true

  // Update search store with date range
  if (queryParams.startDate) {
    searchStore.currentDateRange.startDate = new Date(queryParams.startDate)
  }
  if (queryParams.endDate) {
    searchStore.currentDateRange.endDate = new Date(queryParams.endDate)
  }

  // Extract filter values from query params
  const filterRecord: Record<string, string> = {}
  FILTER_KEYS.forEach(key => {
    if (queryParams[key]) {
      filterRecord[key] = queryParams[key]
    }
  })

  searchStore.clearFilter()
  if (Object.keys(filterRecord).length > 0) {
    searchStore.applyFiltersFromRecord(filterRecord)
  }

  searchStore.setSelectedMerchantId(merchantId)

  searchStore.closeSearchDialog()

  const routeQuery: Record<string, string> = {}

  if (queryParams.startDate) {
    routeQuery.startDate = queryParams.startDate
  }
  if (queryParams.endDate) {
    routeQuery.endDate = queryParams.endDate
  }

  FILTER_KEYS.forEach(key => {
    if (queryParams[key]) {
      routeQuery[key] = queryParams[key]
    }
  })

  router.push({
    name: 'merchantConfigurationTransactionSearch',
    params: { id: merchantId },
    query: routeQuery,
  })
}

watch(
  () => searchStore.isSearchDialogVisible,
  newVisible => {
    if (newVisible && formRef.value) {
      // Initialize filters when dialog opens
      formRef.value.initializeFilters()
      searchPerformed.value = false
    } else if (!newVisible && formRef.value) {
      if (searchPerformed.value) {
        searchPerformed.value = false
        return
      }

      searchStore.clearSelectedMerchant()

      if (!searchStore.hasActiveFilter()) {
        formRef.value.clearSearch()
      }
    }
  },
)
</script>
